import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useCreateHiddenWordMutation,
  useDeleteHiddenWordMutation,
  useUpdateHiddenWordMutation,
} from 'store/api/yay/post'

import { Link, useNavigate } from 'features/i18n/router'
import usePageModal from 'features/Modal/hooks/usePageModal'
import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'

import Button from 'components/Button'
import { Input, SettingItem, Switch } from 'components'

import { MODAL_MODE } from '../config'

import './style.scss'

const KeywordDetailsModal = ({
  id,
  keyword = '',
  context = ['public_timeline', 'following_timeline', 'group_timeline'],
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { generatePageModalUrl } = usePageModal()
  const { showConfirmBox, showErrorBox } = useGlobalConfirmBox()
  const [createKeyword] = useCreateHiddenWordMutation()
  const [updateKeyword] = useUpdateHiddenWordMutation()
  const [deleteKeyword] = useDeleteHiddenWordMutation()

  const [filters, setFilters] = useState({
    keyword: keyword,
    public: context.includes('public_timeline'),
    following: context.includes('following_timeline'),
    group: context.includes('group_timeline'),
  })

  const isCreation = useMemo(() => !id, [id])

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }))

  const onSave = useCallback(async () => {
    if (filters.keyword.length < 2) {
      showErrorBox({
        content: t('keyword_details.error.minimum_character', {
          limit: 2,
        }),
      })
      return
    }
    if (filters.keyword.length > 20) {
      showErrorBox({
        content: t('keyword_details.error.exceeded_character_limit', {
          limit: 20,
        }),
      })
      return
    }
    const context = []
    if (filters.public) {
      context.push('public_timeline')
    }
    if (filters.following) {
      context.push('following_timeline')
    }
    if (filters.group) {
      context.push('group_timeline')
    }

    if (isCreation) {
      const createKeywordResponse = await createKeyword({
        keyword: filters.keyword,
        context,
      })

      if (createKeywordResponse?.['error']?.status === 409) {
        showErrorBox({
          content: t('keyword_details.error.duplicated'),
        })
        return
      }
    } else {
      await updateKeyword({
        id,
        context,
      })
    }

    navigate(generatePageModalUrl(MODAL_MODE.MUTE_KEYWORDS))
  }, [createKeyword, updateKeyword, isCreation, filters])

  const isSaveDisabled = useMemo(
    () =>
      !(
        filters.keyword && Object.values(filters).filter((f) => !!f).length > 1
      ),
    [filters]
  )

  return (
    <div className="Modal__wrapper KeywordDetailsModal">
      <div className="Modal__header">
        <Link
          className="Modal__header__close Modal__header__close--back"
          to={generatePageModalUrl(MODAL_MODE.MUTE_KEYWORDS)}
        />

        <h2 className="Modal__header__h2">{t('keyword_filter_page.title')}</h2>
      </div>

      <div className="Modal__body">
        <Input
          disabled={!isCreation}
          fullWidth={false}
          value={filters.keyword}
          onChange={(e) => setFilter('keyword', e.target.value)}
        />

        <div>
          <b>{t('keyword_details.hide_settings')}</b>

          <SettingItem label={t('keyword_details.open_timeline')}>
            <Switch
              checked={filters.public}
              onChange={(c) => setFilter('public', c)}
            />
          </SettingItem>

          <SettingItem label={t('keyword_details.follow_timeline')}>
            <Switch
              checked={filters.following}
              onChange={(c) => setFilter('following', c)}
            />
          </SettingItem>

          <SettingItem label={t('keyword_details.circle_timeline')}>
            <Switch
              checked={filters.group}
              onChange={(c) => setFilter('group', c)}
            />
          </SettingItem>
        </div>
      </div>

      <div className="Modal__footer">
        <Button
          disabled={isSaveDisabled}
          buttonStyle="green"
          onClick={() => onSave()}
        >
          {t('common.save')}
        </Button>

        {!isCreation && (
          <Button
            buttonStyle="error-text"
            onClick={() => {
              showConfirmBox({
                content: t('keyword_details.delete.title'),
                confirmBoxProps: {
                  onOk: () => {
                    deleteKeyword({ id })
                    navigate(generatePageModalUrl(MODAL_MODE.MUTE_KEYWORDS))
                  },
                },
              })
            }}
          >
            {t('keyword_details.delete.button')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default KeywordDetailsModal
